import { useTranslation } from "react-i18next";
import React, { useState, useMemo, useCallback, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { Layout } from "@/components/layout";
import { ProductSelectionSection } from "@/components/shared/ProductSelectionSection";
import { SelectedProductsList } from "@/components/products/SelectedProductsList";
import { useInitialProducts, useProductsLookup } from "@/hooks/use-products";
import { useOrderTemplates } from "@/hooks/use-order-templates";
import {
  ProductAvailabilityModal,
  ProductAvailabilityIssue,
} from "@/components/orders/ProductAvailabilityModal";
import { SaveTemplateDialog } from "@/components/orders/SaveTemplateDialog";
import { OrderFormData } from "@/types/order";
import { SelectedProduct } from "@/types/product";
import { useLocation, useSearch } from "wouter";
import { useToast } from "@/hooks/use-toast";

export default function EditOrderTemplatesPage() {
  const { t } = useTranslation();
  const [, navigate] = useLocation();
  const search = useSearch();
  const { toast } = useToast();

  // Get template ID from URL parameters
  const templateIdFromUrl = useMemo(() => {
    const urlParams = new URLSearchParams(search);
    return urlParams.get("template");
  }, [search]);

  // State
  const [selectedTemplateId, setSelectedTemplateId] = useState<string | null>(
    null,
  );
  const [selectedProducts, setSelectedProducts] = useState<SelectedProduct[]>(
    [],
  );
  const [isNewTemplate, setIsNewTemplate] = useState(false);
  const [availabilityIssues, setAvailabilityIssues] = useState<
    ProductAvailabilityIssue[]
  >([]);
  const [showAvailabilityModal, setShowAvailabilityModal] = useState(false);
  const [showSaveTemplateDialog, setShowSaveTemplateDialog] = useState(false);
  // Track template product IDs for lookup
  const [templateProductIds, setTemplateProductIds] = useState<string[]>([]);

  // Data hooks
  const { products, isLoading: productsLoading } = useInitialProducts();
  const { data: templates = [] } = useOrderTemplates();

  // Lookup specific template products for validation
  const { products: templateProducts, isLoading: templateProductsLoading } =
    useProductsLookup(templateProductIds, templateProductIds.length > 0);

  // Get current template
  const currentTemplate = selectedTemplateId
    ? templates.find((t) => t.uid === selectedTemplateId)
    : null;

  // Handle template selection - first step: trigger product lookup
  const handleTemplateSelection = useCallback(
    (templateId: string) => {
      if (templateId === "new-template") {
        setSelectedTemplateId(null);
        setIsNewTemplate(true);
        setSelectedProducts([]);
        setTemplateProductIds([]);
      } else {
        const template = templates.find((t) => t.uid === templateId);
        if (template) {
          setSelectedTemplateId(templateId);
          setIsNewTemplate(false);

          // Extract product IDs to trigger lookup
          const productIds = (template.products || [])
            .map((p) => p.productId)
            .filter(Boolean);
          setTemplateProductIds(productIds);
        }
      }
    },
    [templates],
  );

  // Process template products once they're looked up
  useEffect(() => {
    if (
      !selectedTemplateId ||
      templateProductsLoading ||
      templateProductIds.length === 0
    ) {
      return;
    }

    const template = templates.find((t) => t.uid === selectedTemplateId);
    if (!template) return;

    // Convert template products to SelectedProduct format and track missing products
    const issues: ProductAvailabilityIssue[] = [];
    const processedProducts = (template.products || [])
      .map((tp) => {
        const fullProduct = templateProducts.find(
          (p) => p.uid === tp.productId,
        );
        // Product no longer exists in our catalog or is inactive
        if (!fullProduct || !fullProduct.isActive) {
          issues.push({
            productId: tp.productId || "",
            description: tp.description || "Unknown Product",
            type: "product_removed",
          });
          return null;
        }
        return {
          ...fullProduct,
          quantity: tp.quantity,
          notes: tp.notes || "",
        } as SelectedProduct;
      })
      .filter(Boolean) as SelectedProduct[];

    setSelectedProducts(processedProducts);

    // Show modal if any products were missing
    if (issues.length > 0) {
      setAvailabilityIssues(issues);
      setShowAvailabilityModal(true);
    }
  }, [
    selectedTemplateId,
    templateProducts,
    templateProductsLoading,
    templateProductIds,
    templates,
  ]);

  // Initialize with template from URL or first template if available
  useEffect(() => {
    if (
      templates.length > 0 &&
      !selectedTemplateId &&
      !isNewTemplate &&
      !productsLoading &&
      products &&
      products.length > 0
    ) {
      if (
        templateIdFromUrl &&
        templates.find((t) => t.uid === templateIdFromUrl)
      ) {
        handleTemplateSelection(templateIdFromUrl);
      } else {
        handleTemplateSelection(templates[0].uid);
      }
    }
  }, [
    templates,
    selectedTemplateId,
    isNewTemplate,
    templateIdFromUrl,
    handleTemplateSelection,
    productsLoading,
    products,
  ]);

  // Handle product updates (for the selected products list on the right)
  const handleProductUpdate = useCallback(
    (productId: string, updates: Partial<SelectedProduct>) => {
      setSelectedProducts((prev) =>
        prev.map((p) => (p.uid === productId ? { ...p, ...updates } : p)),
      );
    },
    [],
  );

  // Handle product removal (for the selected products list on the right)
  const handleProductRemove = useCallback((productId: string) => {
    setSelectedProducts((prev) => prev.filter((p) => p.uid !== productId));
  }, []);

  // Convert selected products to OrderFormData format for SaveTemplateDialog
  const getCurrentOrderData = useCallback((): OrderFormData | null => {
    if (selectedProducts.length === 0) return null;

    return {
      title: "",
      description: "",
      fulfillmentType: "delivery" as const,
      deliveryDate: "",
      notes: "",
      products: selectedProducts,
    };
  }, [selectedProducts]);

  return (
    <Layout headerContent={
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">{t("common.admin")}</span>
        <span className="text-muted-foreground">/</span>
        <span className="font-semibold">{t("pages.admin.editOrderTemplates.editTemplate")}</span>
      </div>
    }>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h1 className="text-3xl font-bold">{t("pages.admin.editOrderTemplates.editOrderTemplate")}</h1>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 items-center">
            {currentTemplate && (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium whitespace-nowrap">
                  {t("pages.admin.editOrderTemplates.template")}
                </span>
                <span className="text-sm font-medium">
                  {currentTemplate.name}
                </span>
              </div>
            )}

            <div className="h-6 w-px bg-black mx-2" />

            <Button
              onClick={() => setShowSaveTemplateDialog(true)}
              disabled={selectedProducts.length === 0}
              title={
                selectedProducts.length === 0
                  ? t("pages.admin.editOrderTemplates.pleaseAddProductsTo")
                  : t("pages.admin.editOrderTemplates.saveChangesToTemplate")
              }
            >
              {t("pages.admin.editOrderTemplates.saveChanges")}
            </Button>

            <Button
              variant="outline"
              onClick={() => navigate("/admin/order-templates")}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t("pages.admin.editOrderTemplates.backToTemplates")}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left side - Product catalog */}
          <div className="lg:col-span-2">
            <ProductSelectionSection
              selectedProducts={selectedProducts.map((p) => ({
                productId: p.uid,
                quantity: p.quantity,
                notes: p.notes || "",
                unitPrice: p.currentPrice,
              }))}
              onProductsChange={(quoteProducts) => {
                // Convert back to SelectedProduct[] with proper product lookup
                const updatedProducts = quoteProducts
                  .map((product) => {
                    const existing = selectedProducts.find(
                      (sp) => sp.uid === product.productId,
                    );
                    const fullProduct = products.find(
                      (fp) => fp.uid === product.productId,
                    );

                    if (fullProduct) {
                      return {
                        ...fullProduct,
                        quantity: product.quantity,
                        notes: product.notes || "",
                      } as SelectedProduct;
                    }
                    return existing || null;
                  })
                  .filter(Boolean) as SelectedProduct[];

                setSelectedProducts(updatedProducts);
              }}
              showPricing={true}
            />
          </div>

          {/* Right side - Selected products only */}
          <div className="space-y-6">
            {/* Selected Products */}
            <SelectedProductsList
              selectedProducts={selectedProducts.map((p) => ({
                productId: p.uid,
                quantity: p.quantity,
                notes: p.notes || "",
                unitPrice: p.currentPrice,
              }))}
              onProductQuantityChange={(productId, quantity) => {
                handleProductUpdate(productId, { quantity });
              }}
              onProductRemove={handleProductRemove}
              onClearAll={() => setSelectedProducts([])}
              products={products}
              showPricing={true}
              title={t("common.cart")}
            />
          </div>
        </div>

        {/* Product Availability Modal */}
        <ProductAvailabilityModal
          open={showAvailabilityModal}
          onClose={() => setShowAvailabilityModal(false)}
          issues={availabilityIssues}
        />

        {/* Save Template Dialog */}
        {(() => {
          const orderData = getCurrentOrderData();
          return (
            orderData && (
              <SaveTemplateDialog
                open={showSaveTemplateDialog}
                onOpenChange={setShowSaveTemplateDialog}
                orderData={orderData}
                initialName={currentTemplate?.name || ""}
                initialDescription={currentTemplate?.description || ""}
                existingTemplateId={selectedTemplateId || undefined}
                onSaveTemplate={(updatedTemplate) => {
                  // Close dialog after saving template
                  setShowSaveTemplateDialog(false);
                  // Template was updated successfully
                  toast({
                    title: t("pages.admin.editOrderTemplates.templateUpdatedSuccessfully"),
                    description: `Template "${updatedTemplate.name}" has been updated.`,
                  });
                  // Navigate back to templates page
                  navigate("/admin/order-templates");
                }}
              />
            )
          );
        })()}
      </div>
    </Layout>
  );
}
